import React, { useState, useEffect, useRef } from 'react';
import { VscTrash, VscCloudDownload } from 'react-icons/vsc';
import { importLookup } from '../../api/importLookups';
import PortalDropdown from '../common/PortalDropdown';
import { useSort, sortRows, SortTh } from '../common/SortHeader';
import { getRooms, createRoom, updateRoom, deleteRoom } from '../../api/rooms';
import { getSystems, createSystem, updateSystem, deleteSystem } from '../../api/systems';
import { getSections, createSection, updateSection, deleteSection } from '../../api/sections';
import { getCableTypes, createCableType, updateCableType, deleteCableType } from '../../api/cableTypes';
import { getEmgStopGroups, createEmgStopGroup, updateEmgStopGroup, deleteEmgStopGroup } from '../../api/emgStopGroups';
import './Lookups.css';

const CONFIGS = {
  rooms: {
    title: 'Rooms',
    api: { get: getRooms, create: createRoom, update: updateRoom, remove: deleteRoom },
    fields: [
      { key: 'number', label: 'Number', type: 'number', required: true, display: (v) => v != null ? String(v).padStart(3, '0') : '—' },
      { key: 'text',   label: 'Description', required: true },
    ],
  },
  systems: {
    title: 'Systems',
    api: { get: getSystems, create: createSystem, update: updateSystem, remove: deleteSystem },
    fields: [{ key: 'name', label: 'Name', required: true }],
  },
  sections: {
    title: 'Cable Sections',
    api: { get: getSections, create: createSection, update: updateSection, remove: deleteSection },
    fields: [
      { key: 'number', label: 'Number', type: 'number', required: true },
      { key: 'text',   label: 'Description', required: true },
    ],
  },
  cableTypes: {
    title: 'Cable Types',
    api: { get: getCableTypes, create: createCableType, update: updateCableType, remove: deleteCableType },
    fields: [{ key: 'name', label: 'Name', required: true }],
  },
  emgGroups: {
    title: 'EMG Stop Groups',
    api: { get: getEmgStopGroups, create: createEmgStopGroup, update: updateEmgStopGroup, remove: deleteEmgStopGroup },
    fields: [
      { key: 'name', label: 'Name', required: true },
      { key: 'text', label: 'Description' },
    ],
  },
};

const emptyDraft = (fields) => Object.fromEntries(fields.map(f => [f.key, '']));

const rowNav = (e, trEl, { save, cancel, up, down }) => {
  const cells   = [...trEl.querySelectorAll('td[data-cell]')];
  const curCell = cells.find(td => td.contains(document.activeElement));
  const idx     = cells.indexOf(curCell);
  const goTo    = (i) => { if (i < 0 || i >= cells.length) return false; cells[i].querySelector('input, select')?.focus(); return true; };

  if (e.key === 'F2') {
    e.preventDefault();
    const el = e.target;
    if (el.type === 'number') { const v = el.value; el.value = ''; el.value = v; }
    else { try { const len = el.value?.length ?? 0; el.setSelectionRange(len, len); } catch {} }
    return;
  }
  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { if (e.target.tagName === 'SELECT') return; }
  if (e.key === 'ArrowUp')   { e.preventDefault(); up?.(idx); return; }
  if (e.key === 'ArrowDown') { e.preventDefault(); down?.(idx); return; }
  if (e.key === 'Enter')     { if (e.target.tagName === 'SELECT') return; e.preventDefault(); if (down) down(idx); else save(); return; }
  if (e.key === 'Tab') {
    e.preventDefault();
    if (e.shiftKey) goTo(idx - 1); else if (!goTo(idx + 1)) save();
    return;
  }
  if (e.key === 'Escape') { e.preventDefault(); cancel(); return; }
  if (e.key === 'ArrowRight') {
    const el = document.activeElement;
    if (el && el.tagName === 'INPUT' && el.type !== 'number' && el.selectionStart !== null && el.selectionEnd < el.value.length) return;
    e.preventDefault(); cells[idx + 1]?.querySelector('input, select')?.focus(); return;
  }
  if (e.key === 'ArrowLeft') {
    const el = document.activeElement;
    if (el && el.tagName === 'INPUT' && el.type !== 'number' && el.selectionStart !== null && el.selectionStart > 0) return;
    e.preventDefault(); cells[idx - 1]?.querySelector('input, select')?.focus(); return;
  }
};

function LookupPanel({ project, type, role, projects, isActive }) {
  const config = CONFIGS[type];
  const [items, setItems]         = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft]         = useState({});
  const [newRow, setNewRow]       = useState({});
  const [error, setError]         = useState('');
  const [importOpen, setImportOpen]     = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult]   = useState(null);
  const newRowRef   = useRef(null);
  const editRowRef  = useRef(null);
  const importRef   = useRef(null);
  const sortInitRef = useRef(false);
  const undoRef      = useRef([]);
  const redoRef      = useRef([]);
  const beforeMapRef = useRef({});
  const undoRedoRef  = useRef(null);

  const sel = e => e.target.select();

  const pushHistory = (id, before, after) => {
    if (!before || JSON.stringify(before) === JSON.stringify(after)) return;
    undoRef.current = [{ id, before, after }, ...undoRef.current].slice(0, 30);
    redoRef.current = [];
  };

  const canEdit   = ['admin','engineer','manager','foreman'].includes(role);
  const canImport = ['admin','engineer','manager'].includes(role);
  const [sort, toggleSort] = useSort(config?.fields[0]?.key ?? null);

  useEffect(() => {
    if (!project || !config) return;
    config.api.get(project._id).then(r => {
      setItems(sortRows(r.data, sort, (item, key) => item[key] ?? null));
      sortInitRef.current = true;
    }).catch(() => {});
    setEditingId(null);
    setDraft({});
    setNewRow(emptyDraft(config.fields));
    setError('');
    setImportResult(null);
  }, [project?._id, type]);

  useEffect(() => {
    if (!sortInitRef.current) return;
    setItems(prev => sortRows(prev, sort, (item, key) => item[key] ?? null));
  }, [sort.key, sort.dir]);

  useEffect(() => {
    if (!isActive) return;
    const onKeyDown = async (e) => {
      if (!e.ctrlKey || (e.key !== 'z' && e.key !== 'y')) return;
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') && !el.closest('.module-wrap')) return;
      e.preventDefault();
      if (e.key === 'z') await undoRedoRef.current?.undo();
      else await undoRedoRef.current?.redo();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isActive]);

  const handleImport = async (srcProject) => {
    setImportOpen(false);
    setImportLoading(true);
    setImportResult(null);
    try {
      const res = await importLookup(type, srcProject._id, project._id);
      const { imported, skipped, breakersImported } = res.data;
      let msg = `Imported ${imported}`;
      if (breakersImported) msg += ` (+ ${breakersImported} breakers)`;
      if (skipped) msg += `, skipped ${skipped} duplicates`;
      setImportResult({ ok: true, msg });
      config.api.get(project._id).then(r => setItems(sortRows(r.data, sort, (item, key) => item[key] ?? null))).catch(() => {});
    } catch (e) {
      setImportResult({ ok: false, msg: e.response?.data?.msg || 'Import failed' });
    } finally {
      setImportLoading(false);
    }
  };

  const focusEditRow = (colIdx = 0) =>
    setTimeout(() => {
      const cells = [...(editRowRef.current?.querySelectorAll('td[data-cell]') || [])];
      (cells[colIdx] ?? cells[0])?.querySelector('input, select')?.focus();
    }, 0);

  const startEdit = (item, colIdx = 0) => {
    if (!canEdit) return;
    beforeMapRef.current[item._id] = Object.fromEntries(config.fields.map(f => [f.key, item[f.key] ?? '']));
    setEditingId(item._id);
    setDraft(Object.fromEntries(config.fields.map(f => [f.key, item[f.key] ?? ''])));
    setError('');
    setTimeout(() => {
      if (!editRowRef.current) return;
      const tds = [...editRowRef.current.querySelectorAll('td')];
      const focusable = tds[colIdx]?.querySelector('input, select')
        ?? editRowRef.current.querySelector('td[data-cell] input, td[data-cell] select');
      focusable?.focus();
    }, 0);
  };

  const cancelEdit = (itemId) => {
    delete beforeMapRef.current[itemId];
    setEditingId(null); setDraft({});
    if (itemId) setTimeout(() => document.querySelector(`tr[data-row-id="${itemId}"]`)?.focus(), 0);
  };

  const flushSave = async (id, d) => {
    if (!id || !d) return;
    try {
      const res = await config.api.update(id, d);
      setItems(prev => prev.map(i => i._id === id ? res.data : i));
    } catch (e) { setError(e.response?.data?.msg || 'Save failed.'); }
  };

  const commitEdit = async () => {
    if (!editingId) return;
    const id = editingId;
    pushHistory(id, beforeMapRef.current[id], draft);
    delete beforeMapRef.current[id];
    await flushSave(id, draft);
    setEditingId(null); setDraft({});
    setTimeout(() => document.querySelector(`tr[data-row-id="${id}"]`)?.focus(), 0);
  };

  const saveNew = async () => {
    const hasData = config.fields.some(f => String(newRow[f.key] || '').trim());
    if (!hasData) return;
    const required = config.fields.filter(f => f.required);
    if (required.some(f => !String(newRow[f.key] || '').trim())) return;
    setError('');
    try {
      const res = await config.api.create({ project: project._id, ...newRow });
      setItems(prev => [...prev, res.data]);
      setNewRow(emptyDraft(config.fields));
      setTimeout(() => newRowRef.current?.querySelector('input, select')?.focus(), 0);
    } catch (e) { setError(e.response?.data?.msg || 'Save failed.'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this entry?')) return;
    await config.api.remove(id);
    setItems(prev => prev.filter(i => i._id !== id));
    if (editingId === id) { setEditingId(null); setDraft({}); }
  };

  const handleNavRow = (dir, currentId, colIdx = 0) => {
    const isNew = currentId === '_new';
    if (!isNew && editingId === currentId) {
      pushHistory(currentId, beforeMapRef.current[currentId], draft);
      delete beforeMapRef.current[currentId];
      flushSave(currentId, draft);
    } else if (isNew) saveNew();

    if (isNew) {
      if (dir === 'up' && sorted.length > 0) {
        startEdit(sorted[sorted.length - 1], colIdx);
      }
      return;
    }
    const idx = sorted.findIndex(i => i._id === currentId);
    if (dir === 'up') {
      if (idx > 0) {
        startEdit(sorted[idx - 1], colIdx);
      }
      // else: already at top — stay
    } else {
      if (idx < sorted.length - 1) {
        startEdit(sorted[idx + 1], colIdx);
      } else {
        setEditingId(null); setDraft({});
        setTimeout(() => {
          const cells = [...(newRowRef.current?.querySelectorAll('td[data-cell]') || [])];
          ((cells[colIdx] ?? cells[0])?.querySelector('input, select'))?.focus();
        }, 0);
      }
    }
  };

  const sorted = items;

  undoRedoRef.current = {
    undo: async () => {
      if (!undoRef.current.length) return;
      const [entry, ...rest] = undoRef.current;
      undoRef.current = rest;
      redoRef.current = [entry, ...redoRef.current].slice(0, 30);
      if (editingId === entry.id) { setEditingId(null); setDraft({}); }
      await flushSave(entry.id, entry.before);
    },
    redo: async () => {
      if (!redoRef.current.length) return;
      const [entry, ...rest] = redoRef.current;
      redoRef.current = rest;
      undoRef.current = [entry, ...undoRef.current].slice(0, 30);
      if (editingId === entry.id) { setEditingId(null); setDraft({}); }
      await flushSave(entry.id, entry.after);
    },
  };

  if (!project) return <div className="empty-state">Select a project first.</div>;
  if (!config)  return <div className="empty-state">Unknown table type.</div>;

  const otherProjects = (projects || []).filter(p => p._id !== project._id);

  return (
    <div className="module-wrap">
      <div className="module-toolbar">
        <span className="module-toolbar-title">{config.title}</span>
        {canImport && (
          <div className="import-btn-wrap" ref={importRef}>
            <button
              className="btn-icon"
              title="Import from another project"
              disabled={importLoading}
              onClick={() => setImportOpen(v => !v)}
            >
              <VscCloudDownload size={15} />
            </button>
            <PortalDropdown anchor={importRef} open={importOpen} onClose={() => setImportOpen(false)} className="import-dropdown">
              <div className="import-dropdown-title">Import from project</div>
              {otherProjects.length === 0 && (
                <p className="import-dropdown-muted">No other projects</p>
              )}
              {otherProjects.map(p => (
                <button key={p._id} className="import-dropdown-item" onClick={() => handleImport(p)}>
                  {p.name}
                </button>
              ))}
            </PortalDropdown>
          </div>
        )}
        {importResult && (
          <span className={`import-result${importResult.ok ? '' : ' import-result-error'}`}>
            {importResult.msg}
          </span>
        )}
      </div>
      {error && <div className="row-error">{error}</div>}
      <div className="module-body">
        <table className="data-table">
          <thead>
            <tr>
              {config.fields.map(f => (
                <SortTh key={f.key} sortKey={f.key} sort={sort} onSort={toggleSort}>{f.label}</SortTh>
              ))}
              {canEdit && <th style={{ width: 36 }}></th>}
            </tr>
          </thead>
          <tbody>
            {sorted.map(item => {
              if (editingId === item._id) {
                return (
                  <tr key={item._id} ref={editRowRef} className="row-editing"
                    onKeyDown={e => rowNav(e, e.currentTarget, {
                      save:   commitEdit,
                      cancel: () => cancelEdit(item._id),
                      up:     (ci) => handleNavRow('up',   item._id, ci),
                      down:   (ci) => handleNavRow('down', item._id, ci),
                    })}>
                    {config.fields.map(f => (
                      <td key={f.key} data-cell>
                        <input type={f.type || 'text'} value={draft[f.key] ?? ''}
                          onChange={e => setDraft(p => ({ ...p, [f.key]: e.target.value }))}
                          onFocus={sel} />
                      </td>
                    ))}
                    {canEdit && (
                      <td className="cell-actions">
                        <button className="btn-icon btn-icon-danger" title="Delete"
                          onClick={() => handleDelete(item._id)}>
                          <VscTrash size={13} />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              }
              return (
                <tr key={item._id}
                  data-row-id={item._id}
                  tabIndex={canEdit ? -1 : undefined}
                  className={canEdit ? 'data-row-click' : ''}
                  onClick={e => {
                    if (!canEdit) return;
                    const tds = [...e.currentTarget.querySelectorAll('td')];
                    startEdit(item, Math.max(0, tds.indexOf(e.target.closest('td'))));
                  }}
                  onKeyDown={!canEdit ? undefined : e => {
                    if (e.key === 'Escape') { e.preventDefault(); return; }
                    if (e.key === 'Enter' || e.key === 'F2') {
                      e.preventDefault(); startEdit(item, 0);
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const idx = sorted.findIndex(i => i._id === item._id);
                      if (idx < sorted.length - 1)
                        document.querySelector(`tr[data-row-id="${sorted[idx+1]._id}"]`)?.focus();
                      else
                        newRowRef.current?.querySelector('input, select')?.focus();
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      const idx = sorted.findIndex(i => i._id === item._id);
                      if (idx > 0)
                        document.querySelector(`tr[data-row-id="${sorted[idx-1]._id}"]`)?.focus();
                    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                      e.preventDefault(); startEdit(item, 0);
                    }
                  }}>
                  {config.fields.map(f => <td key={f.key}>{f.display ? f.display(item[f.key]) : (item[f.key] ?? '—')}</td>)}
                  {canEdit && <td />}
                </tr>
              );
            })}

            {/* Always-present new row at the bottom */}
            {canEdit && (
              <tr ref={newRowRef} className="row-editing row-new"
                onKeyDown={e => rowNav(e, e.currentTarget, {
                  save:   saveNew,
                  cancel: () => setNewRow(emptyDraft(config.fields)),
                  up:     (ci) => handleNavRow('up', '_new', ci),
                  down:   null,
                })}>
                {config.fields.map((f, fi) => (
                  <td key={f.key} data-cell>
                    <input type={f.type || 'text'} value={newRow[f.key] ?? ''}
                      placeholder={f.label}
                      onChange={e => setNewRow(p => ({ ...p, [f.key]: e.target.value }))}
                      onFocus={sel} />
                  </td>
                ))}
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default LookupPanel;
